import SpeedometerModel from './speedometer/speedometer-model'
import _ from 'lodash'
import {sectionEndpoint} from 'scoreboard/components/bootslide/object-selector-menu'
import i18n from 'scoreboard/components/i18n'
import createWob from './create-wob'
import option from 'scoreboard/components/bootslide/menu-icon-option'
import buildDatasetFieldSelectorMenu from 'scoreboard/components/bootslide/build-dataset-field-selector-menu'
import { LINEAR_GAUGE, SPEEDOMETER } from './widget-type'
import LinearGaugeModel from './linear-gauge/linear-gauge-model'
import defaultDatasetValueWidgetAggregationType from './default-dataset-value-widget-aggregation-type'

/**
 * Creates a menu item and submenu for adding a speedometer widget to a
 * dashboard.
 *
 * @param {String} section - the section of the domain object that will be
 * selected (eg 'scorecards' to select a scorecard item)
 *
 * @param {'speedometer'|'linear-gauge'} widgetType - the type of widget (either
 * WidgetType.SPEEDOMETER or WidgetType.LINEAR_GAUGE)
 *
 * @param {AddDomainObjectEndpoint} [AddOption] -the UI component used to select
 * a domain object to add to the dashboard. Not necessary for datasets, which
 * uses buildDatasetFieldSelectorMenu
 *
 * @param {Object} defaultProperties - a function that returns the default
 * properties for the widget, regardless of the section
 *
 * @param {Object} bootslideMenu - the bootslide menu for adding widgets
 */
function createGaugeMenu (
	section,
	widgetType,
	AddOption,
	defaultProperties,
	bootslideMenu
) {
	let menuKey = `dashboards.dashboard.${_.camelCase(widgetType)}.${section}`
	let menuLabel = i18n.get(menuKey)
	let iconId = `add-widget-${widgetType}`
	if (section === 'datasets') {
		return buildDatasetFieldSelectorMenu({
			menuLabel,
			iconId,
			bootslideMenu,
			fieldSelectorTargetId: `dataset-${widgetType}-field`,
			fieldSelectorOptions: {
				allowTotal: true,
				fieldListFilter
			},
			onFieldSelect: selected => {
				addDatasetWidget(
					{bootslideMenu, selected, widgetType, defaultProperties})
			}
		})
	}
	return {
		label: i18n.get(menuLabel),
		content: option(iconId, menuLabel),
		contentEndpoint: sectionEndpoint(bootslideMenu, section, AddOption)
	}
}

function addDatasetWidget ({ bootslideMenu, selected, widgetType, defaultProperties }) {
	let propsFromSelected =
		_.omit(selected, 'dataset', 'field')

	let properties = _.extend(
		propsFromSelected,
		defaultProperties,
		{
			section: 'datasets',
			domainObjectId: selected.dataset.id,
			startingDatasetId: selected.dataset.id,
			dataSourceType: 'basic',
			aggregationType: defaultDatasetValueWidgetAggregationType(selected.field),
			thresholdMode: 'none'
		})

	const ModelConstructor = getModelConstructor(widgetType)
	var widget = new ModelConstructor({ properties }, {
		domainObject: selected.dataset
	})

	createWob(bootslideMenu.canvas, widget, { fetchData: true }, bootslideMenu)
}

function getModelConstructor (widgetType) {
	switch (widgetType) {
		case SPEEDOMETER:
			return SpeedometerModel
		case LINEAR_GAUGE:
			return LinearGaugeModel
		default:
			throw new Error(`Unknown widget type: ${widgetType}`)
	}
}

/**
 * Predicate for filtering fields in DatasetFieldSelector. Only returns true
 * for dataset fields that are appropriate to use in a speedometer widget
 *
 * @param {DatasetField} f
 * @param {Dataset} datasetModel
 */
function fieldListFilter (f, datasetModel) {
	// TODO appropriate field filter for speedometer. See create-dataset-value-shape-menu.js
	// to see what bubbles allow (probably not the same as speedometers)
	// This can probably be shared with linear gauges
	return true
}

export default createGaugeMenu

createGaugeMenu.__filename__ = 'dashboards/widgets/create-gauge-menu.js'
